import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImageIcon, Film, Upload, Link as LinkIcon } from "lucide-react";
import { deriveVideoThumbnail } from "@/lib/mediaUrl";

const empty = {
    kind: "image",
    url: "",
    title: "",
    thumbnail_url: "",
    display_order: 0,
    is_active: true,
};

function MediaEditor({ open, onOpenChange, initial, onSaved }) {
    const isEdit = Boolean(initial?.id);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [imageSource, setImageSource] = useState("upload"); // "upload" | "url"
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setForm(initial ? { ...empty, ...initial } : empty);
            // For brand-new image entries default to Upload; otherwise default to
            // URL so admins can see the link they originally used.
            setImageSource(initial?.url ? "url" : "upload");
        }
    }, [open, initial]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({
            ...f,
            [name]: type === "checkbox" ? checked
                : name === "display_order" ? Number(value)
                : value,
        }));
    };

    const onPickFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/admin/media/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const url = data.public_url || data.hero_url || "";
            if (!url) throw new Error("Upload response missing public_url");
            setForm((prev) => ({ ...prev, url }));
            toast.success("Uploaded");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.url.trim()) {
            toast.error("URL is required");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/admin/media-gallery/${initial.id}`, form);
                toast.success("Media updated");
            } else {
                await api.post("/admin/media-gallery", form);
                toast.success("Media added");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    // Best-effort preview thumbnail
    const previewThumb =
        form.thumbnail_url ||
        (form.kind === "video" ? deriveVideoThumbnail(form.url) : form.url);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="media-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        {isEdit ? "Edit media" : "Add to gallery"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Type</Label>
                            <Select
                                value={form.kind}
                                onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}
                            >
                                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="media-form-kind">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="image">Image</SelectItem>
                                    <SelectItem value="video">Video</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Display order</Label>
                            <Input
                                type="number"
                                name="display_order"
                                value={form.display_order}
                                onChange={onChange}
                                className="mt-1.5 rounded-xl"
                            />
                        </div>
                    </div>
                    {form.kind === "image" ? (
                        <div>
                            <Label>Source</Label>
                            <div className="mt-1.5 inline-flex p-1 rounded-full bg-paper-alt border border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => setImageSource("upload")}
                                    className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${
                                        imageSource === "upload"
                                            ? "bg-brand-500 text-white shadow-cta"
                                            : "text-ink-700 hover:text-brand-600"
                                    }`}
                                    data-testid="media-form-source-upload"
                                >
                                    <Upload className="h-3.5 w-3.5" /> Upload
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setImageSource("url")}
                                    className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${
                                        imageSource === "url"
                                            ? "bg-brand-500 text-white shadow-cta"
                                            : "text-ink-700 hover:text-brand-600"
                                    }`}
                                    data-testid="media-form-source-url"
                                >
                                    <LinkIcon className="h-3.5 w-3.5" /> Use URL
                                </button>
                            </div>

                            {imageSource === "upload" ? (
                                <div className="mt-3">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={onPickFile}
                                        disabled={uploading}
                                        className="block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-brand-500 file:text-white file:px-4 file:py-2 file:font-medium hover:file:bg-brand-600 disabled:opacity-60"
                                        data-testid="media-form-file"
                                    />
                                    {uploading && (
                                        <p className="text-xs text-ink-700 mt-1.5">Uploading…</p>
                                    )}
                                    {form.url && !uploading && (
                                        <p className="text-[11px] text-emerald-700 mt-1.5 break-all">
                                            Uploaded ✓ <span className="text-ink-700">{form.url.split("/").pop()}</span>
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="mt-3">
                                    <Input
                                        name="url"
                                        value={form.url}
                                        onChange={onChange}
                                        placeholder="https://… (public image URL)"
                                        className="rounded-xl"
                                        required
                                        data-testid="media-form-url"
                                    />
                                    <p className="text-[11px] text-ink-700 mt-1">
                                        Paste a public https image URL.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <Label>Video URL</Label>
                            <Input
                                name="url"
                                value={form.url}
                                onChange={onChange}
                                placeholder="https://youtube.com/watch?v=… or https://vimeo.com/… or .mp4"
                                className="mt-1.5 rounded-xl"
                                required
                                data-testid="media-form-url"
                            />
                            <p className="text-[11px] text-ink-700 mt-1">
                                YouTube and Vimeo links are automatically embedded; direct .mp4 / .webm links also work.
                            </p>
                        </div>
                    )}
                    <div>
                        <Label>Title (optional)</Label>
                        <Input
                            name="title"
                            value={form.title || ""}
                            onChange={onChange}
                            className="mt-1.5 rounded-xl"
                            data-testid="media-form-title"
                        />
                    </div>
                    {form.kind === "video" && (
                        <div>
                            <Label>Custom thumbnail (optional)</Label>
                            <Input
                                name="thumbnail_url"
                                value={form.thumbnail_url || ""}
                                onChange={onChange}
                                placeholder="Leave empty to auto-derive (YouTube)"
                                className="mt-1.5 rounded-xl"
                                data-testid="media-form-thumbnail"
                            />
                        </div>
                    )}
                    {previewThumb && (
                        <div className="rounded-xl border border-gray-100 overflow-hidden aspect-[4/3] bg-paper-alt">
                            <img
                                src={previewThumb}
                                alt="Preview"
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                        </div>
                    )}
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            name="is_active"
                            checked={form.is_active}
                            onChange={onChange}
                        />
                        Active (visible on the storefront)
                    </label>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving || uploading || !form.url.trim()}
                            className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                            data-testid="media-form-save"
                        >
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Add"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function MediaGalleryTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const reload = () => api.get("/admin/media-gallery").then(({ data }) => setItems(data));
    useEffect(() => { reload(); }, []);

    const remove = async (id) => {
        if (!confirm("Remove this media from the gallery?")) return;
        try {
            await api.delete(`/admin/media-gallery/${id}`);
            toast.success("Removed");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-4" data-testid="media-gallery-tab">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="font-heading text-2xl">Media gallery</h3>
                    <p className="text-sm text-ink-700">Photos and video URLs surfaced on the homepage and /media page.</p>
                </div>
                <Button
                    onClick={() => { setEditing(null); setOpen(true); }}
                    className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                    data-testid="media-new-btn"
                >
                    <Plus className="h-4 w-4 mr-1.5" /> Add media
                </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((m) => {
                    const cover = m.thumbnail_url || (m.kind === "image" ? m.url : "");
                    return (
                        <div
                            key={m.id}
                            className="rounded-2xl bg-white border border-gray-100 overflow-hidden"
                            data-testid={`media-row-${m.id}`}
                        >
                            <div className="relative aspect-[4/3] bg-paper-alt">
                                {cover ? (
                                    <img
                                        src={cover}
                                        alt={m.title || m.kind}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-ink-700">
                                        <Film className="h-8 w-8" />
                                    </div>
                                )}
                                <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 backdrop-blur text-[10px] font-semibold uppercase tracking-wide text-ink-900">
                                    {m.kind === "video" ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                                    {m.kind}
                                </span>
                                {!m.is_active && (
                                    <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-ink-900/80 text-white text-[10px] font-semibold uppercase">
                                        hidden
                                    </span>
                                )}
                            </div>
                            <div className="p-3 flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="font-medium truncate text-sm">{m.title || "(untitled)"}</div>
                                    <div className="text-[11px] text-ink-700 truncate">{m.url}</div>
                                </div>
                                <div className="flex gap-1.5 shrink-0">
                                    <Button
                                        size="icon"
                                        variant="outline"
                                        className="rounded-full h-8 w-8"
                                        onClick={() => { setEditing(m); setOpen(true); }}
                                        data-testid={`media-edit-${m.id}`}
                                    >
                                        <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="outline"
                                        className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50"
                                        onClick={() => remove(m.id)}
                                        data-testid={`media-delete-${m.id}`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {items.length === 0 && (
                    <div className="sm:col-span-2 lg:col-span-3 text-center py-12 text-ink-700">
                        No media yet — click "Add media" to add a photo or video URL.
                    </div>
                )}
            </div>
            <MediaEditor open={open} onOpenChange={setOpen} initial={editing} onSaved={reload} />
        </div>
    );
}
